<script setup lang="ts">
import { ref, watch } from 'vue';
import { isEmpty } from 'lodash';

import { useAttachmentUpload } from '@satellite/composables/useAttachmentUpload';
import {
  ATTACHMENT_ACCEPTED_MIME_TYPES,
  ATTACHMENT_MAX_SIZE_MB,
  getAttachmentRejection,
  type AttachmentRejectionReason
} from '@satellite/helpers/attachmentUtils';

import type { DatabaseEntities, Maybe, Nullable } from '@nova/core';

defineOptions({ name: 'AttachmentUploadButton' });

const {
  relatedEntity,
  relatedId,
  label,
  deferUpload = false,
  testId = 'attachment'
} = defineProps<{
  /** Which entity the documents hang off, e.g. `DatabaseEntities.YardTask`. */
  relatedEntity: DatabaseEntities;
  /** The entity's id. Nothing is uploaded until there is one. */
  relatedId: Maybe<string>;
  /** Accessible name for the control, e.g. "Upload a file". */
  label: string;
  /**
   * Hands a valid file back through `picked` instead of uploading it, for a create form whose
   * entity has no id yet. The caller holds the file and uploads it once there is one — see
   * `useStagedAttachments` and `uploadStagedAttachments`. The same file rules still apply:
   * what this refuses, it refuses here too.
   */
  deferUpload?: boolean;
  /** Prefix for every `data-testid` this control renders, e.g. `<testId>-upload-button`. */
  testId?: string;
}>();

/**
 * Read-only for the caller: the upload is this control's, but the tiles beside it may want to
 * settle while one is in flight, and only the caller can reach them.
 */
const isUploading = defineModel<boolean>('isUploading', { default: false });

const emit = defineEmits<{
  /** Once per pick, with how many of its files were stored; never with none. */
  uploaded: [count: number];
  /**
   * A valid file for the caller to upload itself, one emit per file in the pick. Only ever
   * emitted while `deferUpload`.
   */
  picked: [file: File];
  /**
   * Files refused before any request was made; the wording is the caller's. Once per distinct
   * reason rather than per file, so a pick of ten oversized images is still one thing to fix.
   */
  rejected: [reason: AttachmentRejectionReason, maxSizeMb: number];
  error: [error: unknown];
}>();

const fileInput = ref<Nullable<HTMLInputElement>>(null);

const { uploadAttachments, isUploading: isRequestPending } = useAttachmentUpload({
  relatedEntity: () => relatedEntity,
  relatedId: () => relatedId,
  onError: error => emit('error', error)
});

watch(isRequestPending, pending => {
  isUploading.value = pending;
});

/**
 * A whole pick at a time: images, PDFs or a mix of both. What the picker offers is one thing,
 * what the app is willing to attach is another, so every file is screened here before any
 * request is made, and the ones that pass still go up even when others in the pick did not.
 *
 * No `capture` attribute: leaving the source to the OS is the point. A hint here is what made
 * mobile show its own file/photo picker on top of the app's, forcing a pick twice over.
 */
async function handleFileChange(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);

  // Reset first so picking the same file twice in a row still fires `change`.
  input.value = '';

  if (isEmpty(files)) {
    return;
  }

  const accepted: File[] = [];
  const rejectedReasons = new Set<AttachmentRejectionReason>();

  files.forEach(file => {
    const rejection = getAttachmentRejection(file);

    if (rejection) {
      rejectedReasons.add(rejection);
    } else {
      accepted.push(file);
    }
  });

  rejectedReasons.forEach(reason => emit('rejected', reason, ATTACHMENT_MAX_SIZE_MB));

  // Checked after the file rules, so a deferring caller never receives what would be refused.
  if (deferUpload) {
    accepted.forEach(file => emit('picked', file));
    return;
  }

  const storedCount = await uploadAttachments(accepted);

  if (storedCount > 0) {
    emit('uploaded', storedCount);
  }
}
</script>

<template>
  <!--
    Out of the accessibility tree on purpose: the button beside it carries the label and the
    action, so an unnamed file input would only be a second, mute control to land on.
  -->
  <input
    ref="fileInput"
    type="file"
    multiple
    class="d-sr-only"
    aria-hidden="true"
    tabindex="-1"
    :accept="ATTACHMENT_ACCEPTED_MIME_TYPES"
    :data-testid="`${testId}-file-input`"
    @change="handleFileChange" />

  <!-- A deferring caller has no id to wait for, so only a real upload is gated on one. -->
  <v-btn
    variant="text"
    class="attachment-upload-button"
    :disabled="isRequestPending || (!deferUpload && !relatedId)"
    :loading="isRequestPending"
    :aria-label="label"
    :data-testid="`${testId}-upload-button`"
    @click="fileInput?.click()">
    <v-icon icon="mdi-tray-arrow-up" size="24" />
  </v-btn>
</template>

<style lang="scss" scoped>
/* Sized to match the attachment tiles it sits alongside. */
.attachment-upload-button {
  width: 72px;
  height: 72px;
  min-width: 0;
  padding: $spacing-1;
  border: 1px dashed $color-line-divider;
  border-radius: $default-border-radius;
  background-color: transparent;
  color: inherit;
}
</style>
